import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";

import isNil from "lodash/isNil";
import type { ImageURISource } from "react-native";
import { ImageBackground } from "react-native";

import {
  AnalyticsFieldName,
  HomeAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import type { UnencryptedStorageKey } from "@/hooks/useUnencryptedStorage";
import { useUnencryptedStorage } from "@/hooks/useUnencryptedStorage";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import TopRightDismissButton from "@/ui/components/home/TopRightDismissButton";
import { BaseCard, Box } from "@/ui/layout";
import { LayoutToken } from "@/ui/theme/tokens";
import type { ColorToken } from "@/ui/theme/tokens";
import { openUrl } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";

const CardDimensions = {
  Height: 260,
  WebMaxWidth: 375,
} as const;

const NoopStorageKey = "noop";

interface HomeContentImageProps extends PropsWithChildren {
  imageHeight?: number;
  imageSource: ImageURISource;
}

const HomeContentImage = ({
  children,
  imageHeight = CardDimensions.Height,
  imageSource,
}: HomeContentImageProps) => {
  return (
    <ImageBackground
      alt="Hero Background"
      resizeMode="cover"
      source={imageSource}
      style={{
        height: imageHeight,
        overflow: "hidden",
      }}
    >
      {children}
    </ImageBackground>
  );
};

interface HomeResourcesCardProps extends ViewWithTestID, PropsWithChildren {
  analyticsSource: string;
  analyticsTitle?: string;
  cardHeight?: number;
  contentUrl: string;
  dismissColor?: ColorToken;
  shouldShowDismissButton?: boolean;
  storageKey: "noop" | UnencryptedStorageKey;
}

const HomeResourcesCard = ({
  analyticsSource,
  analyticsTitle,
  cardHeight = CardDimensions.Height,
  children,
  contentUrl,
  dismissColor,
  shouldShowDismissButton = true,
  storageKey,
  testID,
}: HomeResourcesCardProps) => {
  const { track } = useAnalytics();
  const { captureException } = useAppMonitor();

  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const { getUnencryptedStringValue, saveUnencryptedStringValue } =
    useUnencryptedStorage();
  useEffect(() => {
    if (storageKey === NoopStorageKey) return;
    const getIsDismissed = async () => {
      const storedValue = await getUnencryptedStringValue(storageKey);
      if (isNil(storedValue)) {
        return false;
      }
      setIsDismissed(JSON.parse(storedValue));
      // eslint-disable-next-line sonarjs/no-redundant-jump -- to enforce tsconfig's noImplicitReturn
      return;
    };

    void getIsDismissed();
  }, [getUnencryptedStringValue, storageKey]);

  const trackEvent = useCallback(
    (eventName: string) => {
      track(eventName, {
        [AnalyticsFieldName.Component]: HomeResourcesCard.name,
        [AnalyticsFieldName.Source]: analyticsSource,
        [AnalyticsFieldName.Title]: analyticsTitle ?? "",
      });
    },
    [analyticsSource, analyticsTitle, track],
  );

  const handlePress = async () => {
    trackEvent(HomeAnalyticsEvent.ResourceCardPressed);
    void openUrl({
      onError: (error?: Error) => {
        captureException(
          new Error(
            `Failed to open article from Home screen: ${analyticsTitle}`,
            { cause: error },
          ),
        );
      },
      url: contentUrl,
    });
  };

  const handleDismiss = async () => {
    if (storageKey !== NoopStorageKey) {
      await saveUnencryptedStringValue({
        key: storageKey,
        value: JSON.stringify(true),
      });
    }
    trackEvent(HomeAnalyticsEvent.ResourceCardDismissed);
    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  const [alignSelf, width, maxWidth] = isWeb()
    ? ["center" as const, CardDimensions.WebMaxWidth, "100%"]
    : [undefined, undefined, undefined];

  return (
    <Box
      alignSelf={alignSelf}
      maxWidth={maxWidth}
      paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
      width={width}
    >
      <BaseCard
        hasBorder={false}
        maxHeight={cardHeight}
        onPress={handlePress}
        testID={testID}
        variant="resourceCard"
      >
        {shouldShowDismissButton && (
          <TopRightDismissButton
            color={dismissColor}
            onPress={handleDismiss}
            testID={testID}
          />
        )}
        {children}
      </BaseCard>
    </Box>
  );
};

export { CardDimensions, HomeContentImage, NoopStorageKey };
export type { HomeResourcesCardProps };
export default HomeResourcesCard;
